import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Download, RefreshCw, PlugZap, Save } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function KoboSync() {
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ server_url: "", asset_uid: "", api_token: "" });
  const [assets, setAssets] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [c, u] = await Promise.all([api.get("/kobo/settings"), api.get("/kobo/unmatched")]);
    setCfg(c.data);
    setForm({ server_url: c.data.server_url, asset_uid: c.data.asset_uid, api_token: "" });
    setUnmatched(u.data);
  };
  useEffect(() => { load().catch((e) => toast.error(apiErr(e.response?.data?.detail))); }, []);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) ok(r.data); await load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>;

  return (
    <div data-testid="kobo-page">
      <PageHeader eyebrow={t("Canal de collecte alternatif")} title="KoboToolbox"
        subtitle={t("Déployez le formulaire quotidien Stride dans KoboToolbox (gratuit, hors ligne) et rapatriez les soumissions dans Stride. Le jeton reste côté serveur.")} testid="kobo-header" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5" data-testid="kobo-step1">
          <h2 className="text-sm font-semibold text-slate-900 mb-1">1. {t("Formulaire XLSForm")}</h2>
          <p className="text-xs text-slate-500 mb-3">{t("Les noms des questions sont identiques aux variables Stride : aucune correspondance manuelle. Importez ce fichier dans KoboToolbox (Nouveau › Importer un XLSForm), puis déployez.")}</p>
          <a href={`${BACKEND}/api/kobo/xlsform?volet=epi&auth=${localStorage.getItem("token")}`} className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B]" data-testid="kobo-xlsform-btn">
            <Download className="h-4 w-4" />{t("Télécharger le XLSForm (Épidémiologie)")}
          </a>
        </section>

        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5" data-testid="kobo-step2">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">2. {t("Connexion au serveur Kobo")}</h2>
          <div className="space-y-3">
            <div><Label className="text-xs">{t("Serveur")}</Label><Input value={form.server_url} onChange={(e) => setForm({ ...form, server_url: e.target.value })} placeholder="https://kf.kobotoolbox.org" data-testid="kobo-url" /></div>
            <div><Label className="text-xs">{t("Jeton API")} {cfg.token_set && <span className="text-emerald-700">· {t("configuré")}</span>}</Label><Input type="password" value={form.api_token} onChange={(e) => setForm({ ...form, api_token: e.target.value })} placeholder={cfg.token_set ? "••••••••" : t("Compte › Sécurité › Clé API")} data-testid="kobo-token" /></div>
            <div><Label className="text-xs">{t("Identifiant du formulaire (asset UID)")}</Label><Input value={form.asset_uid} onChange={(e) => setForm({ ...form, asset_uid: e.target.value })} placeholder="aXXXXXXXXXXXXXXXXXXXX" data-testid="kobo-asset" /></div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => run("save", () => api.put("/kobo/settings", form), () => toast.success(t("Paramètres enregistrés.")))} disabled={!!busy} className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="kobo-save">
                {busy === "save" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}{t("Enregistrer")}
              </Button>
              <Button variant="outline" onClick={() => run("test", () => api.post("/kobo/test"), (d) => { setAssets(d.assets); toast.success(`${d.assets.length} ${t("formulaire(s) trouvé(s)")}`); })} disabled={!!busy || !cfg.token_set} className="rounded-md" data-testid="kobo-test">
                {busy === "test" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PlugZap className="h-4 w-4 mr-1.5" />}{t("Tester la connexion")}
              </Button>
            </div>
            {assets.length > 0 && (
              <ul className="text-xs divide-y border rounded-md" data-testid="kobo-assets">
                {assets.map((a) => (
                  <li key={a.uid} className="flex items-center justify-between gap-2 p-2">
                    <span className="truncate"><b>{a.name}</b> <span className="text-slate-400 font-mono">{a.uid}</span> · {a.submissions} {t("soumissions")}</span>
                    <button type="button" className="text-[#F05A28] font-semibold" onClick={() => setForm({ ...form, asset_uid: a.uid })} data-testid={`kobo-pick-${a.uid}`}>{t("Utiliser")}</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 lg:col-span-2" data-testid="kobo-step3">
          <h2 className="text-sm font-semibold text-slate-900 mb-1">3. {t("Synchronisation")}</h2>
          <p className="text-xs text-slate-500 mb-3">{t("Chaque soumission devient une saisie quotidienne Stride, rattachée à l'agent via l'e-mail renseigné dans le formulaire (ou le nom d'utilisateur Kobo de son compte).")} {cfg.last_sync && <>{t("Dernière synchronisation")} : {new Date(cfg.last_sync).toLocaleString()} · {JSON.stringify(cfg.last_result)}</>}</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => run("sync", () => api.post("/kobo/sync", {}), (d) => toast.success(`${d.created} ${t("créée(s)")}, ${d.updated} ${t("mise(s) à jour")}, ${d.unmatched} ${t("non rattachée(s)")}`))} disabled={!!busy || !cfg.token_set || !cfg.asset_uid} className="rounded-md bg-[#0F172A] hover:bg-[#1E293B]" data-testid="kobo-sync">
              {busy === "sync" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}{t("Synchroniser les nouvelles soumissions")}
            </Button>
            <Button variant="outline" onClick={() => run("full", () => api.post("/kobo/sync", { full: true }), (d) => toast.success(`${d.fetched} ${t("soumissions traitées")}`))} disabled={!!busy || !cfg.token_set || !cfg.asset_uid} className="rounded-md" data-testid="kobo-sync-full">{t("Tout resynchroniser")}</Button>
          </div>
          {unmatched.length > 0 && (
            <div className="mt-4" data-testid="kobo-unmatched">
              <p className="text-xs font-semibold text-amber-800 mb-1">{unmatched.length} {t("soumission(s) non rattachée(s) à un compte Stride")}</p>
              <ul className="text-xs text-slate-600 space-y-1 max-h-48 overflow-auto">
                {unmatched.map((u) => <li key={u.kobo_id}>{u.date} · {u.health_zone || "—"} · {u.email || u.submitted_by || t("sans e-mail")}</li>)}
              </ul>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
